// Downloads and decrypts the files of a message (docs/web-konzept.md, section 5.3). The presigned
// links are signed for my.voxie.app/voxie-media/…; the browser asks its own origin for the same
// path, and nginx on web-02 passes it on with the right Host. Links live an hour: an expired one
// is renewed by loading the conversation's messages again, like the app does.

import { ContentKind, decrypt } from "../crypto/contentCipher.ts";
import { decryptMedia } from "../crypto/mediaCipher.ts";
import type { StoredMessage } from "./store.ts";

export type Part = "audio" | "media" | "thumb";

/** The path and query of a presigned link on this origin. */
export function sameOrigin(url: string): string {
  const parsed = new URL(url, location.origin);
  return parsed.pathname.startsWith("/voxie-media/") ? parsed.pathname + parsed.search : url;
}

const cache = new Map<string, Promise<string>>();

/** An object URL of the decrypted part; [refresh] loads fresh links when the stored one expired. */
export function mediaUrl(message: StoredMessage, part: Part, refresh: () => Promise<StoredMessage | undefined>): Promise<string> {
  const key = `${message.response.id}:${part}`;
  let pending = cache.get(key);
  if (!pending) {
    pending = load(message, part, refresh);
    pending.catch(() => cache.delete(key));
    cache.set(key, pending);
  }
  return pending;
}

async function load(message: StoredMessage, part: Part, refresh: () => Promise<StoredMessage | undefined>): Promise<string> {
  let current = message;
  if (expiresSoon(current, part)) current = (await refresh()) ?? current;
  const link = linkOf(current, part);
  if (!link || !current.mck) throw new Error("no link or no key");
  const response = await fetch(sameOrigin(link));
  if (!response.ok) throw new Error(`download ${response.status}`);
  const blob = new Uint8Array(await response.arrayBuffer());
  const plain =
    part === "audio" ? await decrypt(ContentKind.AUDIO, blob, current.mck)
    : part === "thumb" ? await decrypt(ContentKind.THUMB, blob, current.mck)
    : current.response.kind === "image" ? await decrypt(ContentKind.IMAGE, blob, current.mck)
    : await decryptMedia(blob, current.mck);
  const type = part === "audio" ? "audio/ogg" : part === "thumb" ? "image/jpeg" : (current.response.media?.mime ?? "application/octet-stream");
  return URL.createObjectURL(new Blob([plain], { type }));
}

function linkOf(message: StoredMessage, part: Part): string | null | undefined {
  const r = message.response;
  return part === "audio" ? r.audio_url : part === "thumb" ? r.media?.thumb_url : r.media?.url;
}

function expiresSoon(message: StoredMessage, part: Part): boolean {
  const r = message.response;
  const at = part === "audio" ? r.audio_url_expires_at : r.media?.expires_at;
  return !at || Date.parse(at) - Date.now() < (part === "audio" ? 30_000 : 60_000);
}

/** A part this browser already has in plain (a voice message it just recorded): no download. */
export function primeMedia(messageId: string, part: Part, blob: Blob) {
  cache.set(`${messageId}:${part}`, Promise.resolve(URL.createObjectURL(blob)));
}

/** Forgets the object URLs (on sign-out): nothing decrypted stays in memory. */
export function clearMedia() {
  for (const pending of cache.values()) void pending.then(URL.revokeObjectURL, () => undefined);
  cache.clear();
}
